/**
 * GIESTO — cart-drawer head.
 *
 * Drives `<gs-cart-tabs>`, the block `snippets/cart-drawer.liquid` renders directly
 * under the drawer title on the the reference design templates only:
 *
 *   [ Check out                    ]   <- duplicate of the theme's checkout submit
 *   Cart (3)            Favourites (5) <- panel switch
 *
 * ── MORPH CONTRACT ────────────────────────────────────────────────────────────
 * The cart drawer re-renders through the Section Rendering API after every cart
 * change. `assets/component-cart-items.js` morphs it in `hydration` mode, which
 * runs a full morph inside `[data-hydration-key="cart-drawer-inner"]` — the node
 * that contains this element.
 *
 * The Liquid prints `data-skip-node-update` and `data-skip-subtree-update` on
 * `<gs-cart-tabs>`. `assets/morph.js` honours both when the OLD and the NEW node
 * carry them (see `walk` and `updateChildren`), and the server re-renders the same
 * attributes, so the element is walked past untouched: the custom element instance
 * is never re-created, `data-panel` is never reset, and the client-rendered
 * wishlist rows and the two counts are never clobbered. Nothing here is re-derived
 * from the re-rendered markup except the bag count, which is deliberately mirrored
 * out of it below.
 *
 * The element is only removed when the cart empties (the server stops rendering it,
 * so morph drops it) and re-created when the cart refills — the one case where the
 * active tab resetting to "bag" is the correct behaviour.
 *
 * Data source for the wishlist is `assets/giesto-wishlist.js` (`window.GiestoWishlist`
 * + the `giesto:wishlist:change` event). This file never writes to localStorage.
 */
(() => {
  const TAG_NAME = 'gs-cart-tabs';

  // Loaded from a snippet that renders on every page, so a second execution is
  // possible; bail before re-declaring the element.
  if (customElements.get(TAG_NAME)) return;

  const WISHLIST_CHANGE_EVENT = 'giesto:wishlist:change';

  /** Reads a whole number out of a text node, defaulting to 0. */
  const toCount = (/** @type {string | null | undefined} */ text) => {
    const parsed = Number.parseInt((text ?? '').trim(), 10);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  class ArkCartTabs extends HTMLElement {
    /** @type {AbortController | null} */
    #controller = null;

    /** @type {MutationObserver | null} */
    #observer = null;

    /** @type {number} */
    #frame = 0;

    /** @type {HTMLElement[]} */
    #tabs = [];

    /** @type {HTMLElement | null} */
    #panel = null;

    /** @type {HTMLElement | null} */
    #list = null;

    /** @type {HTMLTemplateElement | null} */
    #template = null;

    /** @type {HTMLElement | null} */
    #emptyMessage = null;

    /** @type {HTMLElement | null} */
    #bagCount = null;

    /** @type {HTMLElement | null} */
    #wishlistCount = null;

    connectedCallback() {
      // A morph can relocate a node rather than update it in place, which fires
      // disconnected → connected. Tearing down first keeps exactly one set of
      // listeners and one observer alive at any time.
      this.disconnectedCallback();

      const controller = new AbortController();
      this.#controller = controller;
      const { signal } = controller;

      this.#tabs = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-gs-tab]')));
      this.#panel = this.querySelector('[data-gs-panel]');
      this.#list = this.querySelector('[data-gs-list]');
      this.#template = this.querySelector('template[data-gs-template]');
      this.#emptyMessage = this.querySelector('[data-gs-empty]');
      this.#bagCount = this.querySelector('[data-gs-bag-count]');
      this.#wishlistCount = this.querySelector('[data-gs-wishlist-count]');

      // "0 items saved" — the same localized unit the favourites page uses, so the
      // empty state needs no translation key of its own.
      if (this.#emptyMessage) {
        this.#emptyMessage.textContent = `0 ${this.dataset.unitOther ?? ''}`.trim();
      }

      this.addEventListener('click', this.#onClick, { signal });
      document.addEventListener(WISHLIST_CHANGE_EVENT, this.#renderWishlist, { signal });

      // The bag count lives in markup this element is deliberately excluded from,
      // so watch that markup instead of re-fetching the cart: everything inside
      // `.cart-drawer__inner` is rewritten by the morph, this element aside.
      const scope = this.closest('.cart-drawer__inner');
      if (scope) {
        this.#observer = new MutationObserver(this.#scheduleBagCountSync);
        this.#observer.observe(scope, { childList: true, subtree: true, characterData: true });
      }

      this.#showPanel(this.dataset.panel === 'wishlist' ? 'wishlist' : 'bag');
      this.#syncBagCount();

      // The store is an IIFE on a deferred script, so it may not have run yet.
      if (window.GiestoWishlist) this.#renderWishlist();
      else document.addEventListener('DOMContentLoaded', this.#renderWishlist, { once: true, signal });
    }

    disconnectedCallback() {
      this.#controller?.abort();
      this.#controller = null;

      // A MutationObserver is not an event listener, so `abort()` does not reach it.
      this.#observer?.disconnect();
      this.#observer = null;

      if (this.#frame) cancelAnimationFrame(this.#frame);
      this.#frame = 0;
    }

    /** @param {MouseEvent} event */
    #onClick = (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      // `dataset.gsTab`, matching the `data-gs-tab` the Liquid prints. These two
      // spellings have to be renamed together: a rename pass over the kebab-case
      // attribute does not touch the camelCase property, and the mismatch fails
      // silently — the tab simply stops switching, with no error anywhere.
      const tab = target.closest('[data-gs-tab]');
      if (tab instanceof HTMLElement && this.contains(tab)) {
        this.#showPanel(tab.dataset.gsTab === 'wishlist' ? 'wishlist' : 'bag');
        return;
      }

      const remove = target.closest('[data-gs-remove]');
      if (!(remove instanceof HTMLElement) || !this.contains(remove)) return;

      // The drawer must not treat this as a click on its backdrop.
      //
      // ThemeDrawer listens for clicks on the panel and calls isClickedOutside,
      // which is `!panel.contains(event.target)` (assets/utilities.js). Removing
      // the item detaches this button's row from the document, so by the time
      // the event finishes bubbling to the panel, `event.target` is no longer
      // inside it — the check reads as an outside click and closes the drawer.
      // Stopping propagation here keeps the event from ever reaching that
      // handler; the click was fully handled by us.
      event.stopPropagation();

      const row = remove.closest('[data-id]');
      const id = row instanceof HTMLElement ? row.dataset.id : undefined;
      if (id) window.GiestoWishlist?.remove(id);
    };

    /**
     * Switches the visible panel. The "bag" panel is the theme's own
     * `<cart-items-component>`, a later sibling of this element — the the reference design
     * stylesheet hides it off `[data-panel='wishlist']` rather than this file
     * touching markup it does not own.
     * @param {'bag' | 'wishlist'} panel
     */
    #showPanel = (panel) => {
      this.dataset.panel = panel;

      for (const tab of this.#tabs) {
        tab.setAttribute('aria-selected', String(tab.dataset.gsTab === panel));
      }

      this.#panel?.toggleAttribute('hidden', panel !== 'wishlist');
    };

    /** Coalesces the bursts of mutations a morph produces into one read per frame. */
    #scheduleBagCountSync = () => {
      if (this.#frame) return;
      this.#frame = requestAnimationFrame(() => {
        this.#frame = 0;
        this.#syncBagCount();
      });
    };

    #syncBagCount = () => {
      if (!this.#bagCount) return;

      const scope = this.closest('.cart-drawer__inner');
      // The drawer header badge first — it is this block's own sibling. `[ref="cartItemCount"]`
      // from `snippets/cart-products.liquid` is the fallback. Both carry `cart.item_count`
      // and both disappear together when the cart empties, which reads as 0.
      const source = scope?.querySelector('.theme-drawer__badge') ?? scope?.querySelector('[ref="cartItemCount"]');
      const text = `(${toCount(source?.textContent)})`;

      // Writing unconditionally would re-trigger the observer this write is
      // observed by; the equality check makes that converge immediately.
      if (this.#bagCount.textContent !== text) this.#bagCount.textContent = text;
    };

    #renderWishlist = () => {
      const store = window.GiestoWishlist;
      if (!store) return;

      const items = store.items;

      if (this.#wishlistCount) this.#wishlistCount.textContent = `(${items.length})`;
      this.#emptyMessage?.toggleAttribute('hidden', items.length > 0);

      const list = this.#list;
      const template = this.#template;
      if (!list || !template) return;

      const fragment = document.createDocumentFragment();
      const removeLabel = this.dataset.removeLabel || '';

      for (const item of items) {
        const node = /** @type {DocumentFragment} */ (template.content.cloneNode(true));
        const row = node.querySelector('.gs-wish__row');
        const link = node.querySelector('[data-gs-link]');
        const image = node.querySelector('[data-gs-image]');
        const title = node.querySelector('[data-gs-title]');
        const price = node.querySelector('[data-gs-price]');
        const remove = node.querySelector('[data-gs-remove]');

        if (row instanceof HTMLElement) row.dataset.id = item.id;
        if (link instanceof HTMLAnchorElement) link.href = item.url;
        if (image instanceof HTMLImageElement) {
          if (item.image) {
            image.src = item.image;
            image.alt = item.title;
          } else {
            image.remove();
          }
        }
        if (title instanceof HTMLAnchorElement) {
          title.textContent = item.title;
          title.href = item.url;
        }
        if (price) price.textContent = item.price;
        if (remove) remove.setAttribute('aria-label', `${removeLabel} ${item.title}`.trim());

        fragment.appendChild(node);
      }

      list.replaceChildren(fragment);
    };
  }

  if (!customElements.get(TAG_NAME)) {
    customElements.define(TAG_NAME, ArkCartTabs);
  }
})();
